/**
 * ============================================================================
 * KbTiptapEditor — Editor rico GitBook-like: toolbar + slash menu + blocos
 *                  customizados (callout, steps, toggle, card, embed).
 * ----------------------------------------------------------------------------
 * Fonte de verdade da EDIÇÃO é sempre o JSON do Tiptap (content_json).
 * A camada `content_markdown` é derivada no salvamento (via lib
 * `@/lib/kb/markdown`) e usada pelo pipeline de IA.
 *
 * CHANGELOG:
 *   RELEASE: 2026-07-08 v2.1.0 - Protege cliques em menus/handles do KB para
 *            impedir foco no fim do documento e saltos de scroll.
 *   RELEASE: 2026-07-08 v2.0.0 - Bloco 3: extensions compartilhadas com o
 *            reader, slash menu tipo Notion, blocos GitBook (steps, toggle,
 *            card grid), tipografia `.kb-prose`.
 *   RELEASE: 2026-07-07 v1.0.0 - F1-Fix. Toolbar + blocks essenciais.
 * ============================================================================
 */
import { useEditor, EditorContent } from "@tiptap/react";
import { useEffect, useRef, useState, useCallback } from "react";
import {
  DndContext, PointerSensor, useSensor, useSensors,
  DragOverlay, closestCenter,
  type DragStartEvent, type DragMoveEvent, type DragEndEvent, type DragCancelEvent,
} from "@dnd-kit/core";
import { GripVertical } from "lucide-react";
import { KB_TIPTAP_EXTENSIONS } from "./kbTiptapExtensions";
import { KbSlashMenu } from "./KbSlashMenu";
import { KbBlockHandle } from "./KbBlockHandle";
import { KbTextBubbleMenu } from "./KbTextBubbleMenu";
import { KbLinkHoverCard } from "./KbLinkHoverCard";
import { TextSelection } from "@tiptap/pm/state";

// Toolbar horizontal removida (v3.0.0) — a formatação agora é feita via
// `KbTextBubbleMenu` (aparece ao selecionar texto) e a inserção de blocos via
// slash menu "/" ou pelo handle "+" ao lado de cada bloco.



// ---------------------------------------------------------------------------
// Componente principal
// ---------------------------------------------------------------------------
interface Props {
  value: any; // JSON do Tiptap
  onChange: (json: any) => void;
  editable?: boolean;
  placeholder?: string;
}

export function KbTiptapEditor({ value, onChange, editable = true, placeholder = "Comece a escrever…" }: Props) {
  const editor = useEditor({
    extensions: [
      ...KB_TIPTAP_EXTENSIONS,
      KbSlashMenu,
    ],
    content: value ?? { type: "doc", content: [{ type: "paragraph" }] },
    editable,
    onUpdate: ({ editor }) => onChange(editor.getJSON()),
    editorProps: {
      attributes: {
        class: "kb-prose max-w-none min-h-full outline-none",
      },
    },
  });

  // Sincroniza value externo (ex.: hidratação após load)
  useEffect(() => {
    if (!editor || !value) return;
    const current = editor.getJSON();
    if (JSON.stringify(current) !== JSON.stringify(value)) {
      editor.commands.setContent(value, { emitUpdate: false } as any);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, JSON.stringify(value)]);

  useEffect(() => {
    if (editor) editor.setEditable(editable);
  }, [editor, editable]);

  // Handler: clicar no canvas vazio posiciona o cursor no bloco MAIS PRÓXIMO
  // do clique, sem rolar a página. Antes usávamos focus("end"), o que fazia
  // o Tiptap chamar scrollIntoView e jogar a coluna do editor para o fim do
  // documento — péssima UX se o usuário estava lendo o topo.
  const handleAreaClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!editor || !editable) return;
    const target = e.target as HTMLElement;
    if (target.closest(".ProseMirror")) return;
    if (
      target.closest(
        ".kb-block-handle, [data-kb-editor-ui], button, input, textarea, select, a, [role='button'], [role='menu'], [role='menuitem']",
      )
    ) return;

    // Preserva scroll do container do editor para vencer o autofocus do
    // ProseMirror (evita salto vertical mesmo quando setamos scrollIntoView:false).
    const scrollEl = editor.view.dom.closest("[data-kb-scroll]") as HTMLElement | null;
    const savedTop = scrollEl?.scrollTop ?? 0;
    const savedLeft = scrollEl?.scrollLeft ?? 0;

    const view = editor.view;
    const proseRect = view.dom.getBoundingClientRect();

    // Ajusta X para dentro da coluna de texto (evita cair no gutter esquerdo,
    // onde posAtCoords pode devolver null).
    const clampedX = Math.min(
      Math.max(e.clientX, proseRect.left + 16),
      proseRect.right - 16,
    );
    const clampedY = Math.min(Math.max(e.clientY, proseRect.top + 4), proseRect.bottom - 4);

    let targetPos: number | null = null;
    try {
      const info = view.posAtCoords({ left: clampedX, top: clampedY });
      if (info) targetPos = info.pos;
    } catch { /* ignora */ }

    // Fallback: se o clique é abaixo de todo conteúdo, foca no FIM do último
    // bloco (sem scrollIntoView).
    if (targetPos == null) {
      targetPos = editor.state.doc.content.size;
    }

    const tr = editor.state.tr
      .setSelection(TextSelection.near(editor.state.doc.resolve(targetPos)))
      .setMeta("preventAutoScroll", true)
      .setMeta("addToHistory", false);
    // Impede o scrollIntoView padrão que o Tiptap dispara no focus.
    (tr as any).scrollIntoView = () => tr;
    editor.view.dispatch(tr);
    editor.view.focus();

    const restore = () => {
      if (!scrollEl) return;
      scrollEl.scrollTop = savedTop;
      scrollEl.scrollLeft = savedLeft;
    };
    restore();
    requestAnimationFrame(() => {
      restore();
      requestAnimationFrame(restore);
    });
  };


  const scrollRef = useRef<HTMLDivElement | null>(null);

  // -----------------------------------------------------------------------
  // Estado do Drag & Drop (dnd-kit) — orquestra o arraste de blocos do KB.
  // -----------------------------------------------------------------------
  const [activeDragPos, setActiveDragPos] = useState<number | null>(null);
  const [activeDragLabel, setActiveDragLabel] = useState<string>("");
  const [dropIndicator, setDropIndicator] =
    useState<{ top: number; visible: boolean; insertPos: number } | null>(null);

  // Sensor com threshold — clique curto (< 5px) não dispara drag e continua
  // abrindo o menu contextual normalmente.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  /**
   * Calcula a posição-alvo (top-level) para inserção com base no clientY do
   * cursor. Retorna também o `top` (relativo ao wrapper) onde a linha
   * indicadora deve ser desenhada.
   */
  const computeDropTarget = useCallback(
    (clientX: number, clientY: number): { insertPos: number; indicatorTop: number } | null => {
      if (!editor) return null;
      const view = editor.view;
      const wrapper = scrollRef.current;
      if (!wrapper) return null;
      const wrapRect = wrapper.getBoundingClientRect();

      // Tenta encontrar o bloco top-level mais próximo do cursor
      const posInfo = view.posAtCoords({ left: clientX, top: clientY });
      if (!posInfo) {
        // Fora do editor: decide topo/fundo
        const isAbove = clientY < wrapRect.top + wrapRect.height / 2;
        const doc = view.state.doc;
        const insertPos = isAbove ? 0 : doc.content.size;
        const firstDom = view.nodeDOM(0) as HTMLElement | null;
        const lastPos = doc.childCount > 0 ? doc.content.size - (doc.lastChild?.nodeSize ?? 0) : 0;
        const lastDom = view.nodeDOM(lastPos) as HTMLElement | null;
        const refDom = isAbove ? firstDom : lastDom;
        if (!refDom) return null;
        const r = refDom.getBoundingClientRect();
        const top = (isAbove ? r.top : r.bottom) - wrapRect.top;
        return { insertPos, indicatorTop: top };
      }

      let $pos = view.state.doc.resolve(posInfo.pos);
      while ($pos.depth > 1) $pos = view.state.doc.resolve($pos.before($pos.depth));
      if ($pos.depth < 1 && posInfo.inside >= 0) {
        $pos = view.state.doc.resolve(posInfo.inside + 1);
      }
      if ($pos.depth < 1) return null;

      const before = $pos.before(1);
      const nodeAt = view.state.doc.nodeAt(before);
      if (!nodeAt) return null;

      const dom = view.nodeDOM(before) as HTMLElement | null;
      if (!dom) return null;
      const rect = dom.getBoundingClientRect();
      const midpoint = rect.top + rect.height / 2;
      const insertBefore = clientY < midpoint;
      const insertPos = insertBefore ? before : before + nodeAt.nodeSize;
      const indicatorTop =
        (insertBefore ? rect.top : rect.bottom) - wrapRect.top;
      return { insertPos, indicatorTop };
    },
    [editor]
  );

  const onDragStart = (e: DragStartEvent) => {
    const pos = (e.active.data.current as { pos?: number } | undefined)?.pos;
    if (typeof pos !== "number" || !editor) return;
    setActiveDragPos(pos);
    const node = editor.state.doc.nodeAt(pos);
    setActiveDragLabel(labelForNode(node?.type.name ?? "bloco"));
    scrollRef.current?.classList.add("kb-editor-dragging");
    const dom = editor.view.nodeDOM(pos) as HTMLElement | null;
    if (dom) dom.classList.add("kb-block-dragging");
  };

  const onDragMove = (e: DragMoveEvent) => {
    const activator = e.activatorEvent as PointerEvent;
    const clientX = (activator?.clientX ?? 0) + e.delta.x;
    const clientY = (activator?.clientY ?? 0) + e.delta.y;
    const target = computeDropTarget(clientX, clientY);
    if (target) {
      setDropIndicator({ top: target.indicatorTop, visible: true, insertPos: target.insertPos });
    }
  };

  const cleanupDrag = () => {
    if (activeDragPos !== null && editor) {
      const dom = editor.view.nodeDOM(activeDragPos) as HTMLElement | null;
      if (dom) dom.classList.remove("kb-block-dragging");
    }
    scrollRef.current?.classList.remove("kb-editor-dragging");
    setActiveDragPos(null);
    setActiveDragLabel("");
    setDropIndicator(null);
  };

  const onDragCancel = (_e: DragCancelEvent) => cleanupDrag();

  const onDragEnd = (_e: DragEndEvent) => {
    if (!editor || activeDragPos === null || !dropIndicator) {
      cleanupDrag();
      return;
    }
    const fromPos = activeDragPos;
    const node = editor.state.doc.nodeAt(fromPos);
    if (!node) { cleanupDrag(); return; }
    const nodeSize = node.nodeSize;
    let insertPos = dropIndicator.insertPos;

    // Sem movimento efetivo (destino coincide com origem)
    if (insertPos === fromPos || insertPos === fromPos + nodeSize) {
      cleanupDrag();
      return;
    }

    const json = node.toJSON();
    // Ajusta destino se estiver depois da origem (o delete puxa o offset)
    if (insertPos > fromPos) insertPos -= nodeSize;

    editor
      .chain()
      .focus()
      .command(({ tr }) => {
        tr.delete(fromPos, fromPos + nodeSize);
        tr.insert(insertPos, editor.schema.nodeFromJSON(json));
        return true;
      })
      .run();

    cleanupDrag();
  };

  return (
    <div className="flex flex-col rounded-md border min-h-[600px] bg-background">
      <KbTextBubbleMenu editor={editor} />
      <KbLinkHoverCard editor={editor} />
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={onDragStart}
        onDragMove={onDragMove}
        onDragEnd={onDragEnd}
        onDragCancel={onDragCancel}
      >
        <div
          ref={scrollRef}
          className="relative p-6 pl-16 cursor-text"
          onClick={handleAreaClick}
        >
          <EditorContent editor={editor} />
          <KbBlockHandle
            editor={editor}
            wrapperRef={scrollRef}
            activeDragPos={activeDragPos}
            dropIndicator={dropIndicator ? { top: dropIndicator.top, visible: dropIndicator.visible } : null}
          />
        </div>
        <DragOverlay dropAnimation={null}>
          {activeDragPos !== null ? (
            <div className="flex items-center gap-2 rounded-md border bg-background/95 px-2 py-1 shadow-lg text-xs text-foreground backdrop-blur">
              <GripVertical className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="truncate max-w-[220px]">{activeDragLabel}</span>
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
    </div>
  );
}

// Rótulo curto para o tipo de nó (usado no fantasma do DragOverlay)
function labelForNode(type: string): string {
  const map: Record<string, string> = {
    paragraph: "Parágrafo",
    heading: "Título",
    bulletList: "Lista",
    orderedList: "Lista numerada",
    taskList: "Checklist",
    blockquote: "Citação",
    codeBlock: "Bloco de código",
    horizontalRule: "Divisor",
    callout: "Destaque",
    steps: "Passos",
    toggle: "Toggle",
    cardGrid: "Grade de cards",
    embed: "Embed",
    image: "Imagem",
    table: "Tabela",
  };
  return map[type] ?? "Bloco";
}
